<script lang="ts">
    import { ExportType } from '../../Export/ExportType';
    import { ExportApi } from '../../Export/ExportApi';
    import { captureMessage } from '@sentry/svelte';
    import { api } from '../../../../stores/api';
    import { _ } from 'svelte-i18n';
    import IconPending from '../../../shared/Icons/IconPending.svelte';
    import IconDownload2 from '../../../shared/Icons/IconDownload2.svelte';
    import IconSettings from '../../../shared/Icons/IconSettings.svelte';
    import { openModal } from 'svelte-modals';
    import ModalCustom from '../../../shared/Modals/ModalCustom.svelte';
    import DeliveryListSettings from '../../Settings/DeliveryListSettings/DeliveryListSettings.svelte';
    import { Umami } from '../../../utilities/Umami';

    export let scenarioId: number | null = null;
    export let assignmentId: number | null = null;
    export let showDeliveryListSettings: boolean = false;

    let downloadingInProgress: boolean = false;
    let showDeliveryListOverlay: boolean = false;
    let deliveryListHtml: string = '';

    async function openDeliveryListHtml(scenarioId: number, assignmentId: null | number) {
        downloadingInProgress = true;

        try {
            // Użyj API do pobrania HTML i przypisz bezpośrednio
            deliveryListHtml = await ExportApi.getFileAsText($api, 'delivery_list_export', {
                type: 'text/html',
                scenario_id: scenarioId,
                assignment_ids: assignmentId ? [assignmentId] : undefined,
            });

            if (assignmentId) {
                Umami.event('delivery_list_download_single', { scenario_id: scenarioId, assignment_id: assignmentId });
            } else {
                Umami.event('delivery_list_download_all', { scenario_id: scenarioId });
            }

            // Wyświetl HTML w overlay
            showDeliveryListOverlay = true;
        } catch (error) {
            console.error('Error opening delivery list HTML', error);
            alert($_('error_downloading_file'));
            captureMessage('Error opening delivery list HTML', {
                extra: {
                    error,
                },
            });
        } finally {
            downloadingInProgress = false;
        }
    }

    function closeDeliveryListOverlay() {
        showDeliveryListOverlay = false;
        deliveryListHtml = '';
    }

    function handlePrint() {
        Umami.event('delivery_list_printed', { scenario_id: scenarioId, assignment_id: assignmentId });
        const iframe = document.getElementById('delivery-list-iframe') as HTMLIFrameElement;
        if (iframe && iframe.contentWindow) {
            iframe.contentWindow.print();
        }
    }

    async function download(et: ExportType, scenarioId: number, assignmentId: null | number) {
        downloadingInProgress = true;
        // https://www.programonaut.com/how-to-create-a-download-for-a-zip-file-with-sveltekit-step-by-step/
        let exportFile = await ExportApi.getFile($api, et.getApiEndpoint(), {
            type: et.getType().long,
            scenario_id: scenarioId,
            assignment_ids: assignmentId ? [assignmentId] : undefined,
        }).catch(error => {
            console.error('Error downloading file', error);
            alert($_('error_downloading_file'));
            captureMessage('Error downloading file', {
                extra: {
                    error,
                },
            });
        });

        if (!exportFile) {
            downloadingInProgress = false;
            return;
        }

        // convert exportFile (raw csv file) to blob
        let blob = new Blob([exportFile], { type: et.getType().long });

        let url = window.URL || window.webkitURL;
        let link = url.createObjectURL(blob);

        // generate anchor tag, click it for download and then remove it again
        let a = document.createElement('a');
        a.setAttribute('download', et.getOutputFileName());
        a.setAttribute('href', link);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // revoke the object URL to free up memory
        url.revokeObjectURL(link);
        downloadingInProgress = false;
    }
</script>

<div style="display: flex; align-items: center; column-gap: 6px; overflow: hidden;">
    <div style="display: flex; align-items: center; column-gap: 6px; overflow: hidden;">
        {#if downloadingInProgress}
            <IconPending scale={0.7} spin={true} />
        {:else}
            <IconDownload2
                hover={$_('open_delivery_list_html')}
                scale={0.7}
                disabled={(scenarioId === null && assignmentId===null) || downloadingInProgress}
                on:click={async () => {
                    console.log('Open delivery list HTML for scenario', scenarioId);
                    if (scenarioId === null) {
                        console.warn('No scenarioId, cannot open delivery list');
                        return;
                    }
                    await openDeliveryListHtml(scenarioId, assignmentId);
                }}
            />
        {/if}
    </div>
    {#if showDeliveryListSettings}
    <div>
        <IconSettings
            scale={0.7}
            title={$_('delivery_list_settings')}
            on:click={()=>{
        openModal(ModalCustom, {
            component: DeliveryListSettings,
            componentProps: {
            },
        });
    }} />
    </div>
    {/if}
</div>

{#if showDeliveryListOverlay}
    <div
        class="delivery-list-overlay"
        on:click|self={closeDeliveryListOverlay}
        on:keydown={(e) => e.key === 'Escape' && closeDeliveryListOverlay()}
        role="dialog"
        aria-modal="true"
        tabindex="-1"
    >
        <div class="delivery-list-container">
            <div class="delivery-list-header">
                <button class="btn-print" on:click={handlePrint}>
                    🖨️ {$_('print_ctrl_p')}
                </button>
                <button class="btn-close" on:click={closeDeliveryListOverlay}>
                    ✕ {$_('Close')}
                </button>
            </div>
            <iframe
                id="delivery-list-iframe"
                class="delivery-list-iframe"
                title={$_('delivery_list')}
                srcdoc={deliveryListHtml}
            ></iframe>
        </div>
    </div>
{/if}

<style>
    .delivery-list-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.8);
        z-index: 9999;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 20px;
    }

    .delivery-list-container {
        width: 100%;
        height: 100%;
        max-width: 1200px;
        max-height: 95vh;
        background: white;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
    }

    .delivery-list-header {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 10px;
        padding: 15px;
        background-color: #f8f9fa;
        border-bottom: 1px solid #dee2e6;
    }

    .btn-print,
    .btn-close {
        padding: 10px 20px;
        font-size: 14px;
        cursor: pointer;
        border: none;
        border-radius: 5px;
        font-weight: 500;
        transition: background-color 0.2s;
    }

    .btn-print {
        background-color: #007bff;
        color: white;
    }

    .btn-print:hover {
        background-color: #0056b3;
    }

    .btn-close {
        background-color: #6c757d;
        color: white;
    }

    .btn-close:hover {
        background-color: #545b62;
    }

    .delivery-list-iframe {
        flex: 1;
        width: 100%;
        border: none;
        background: white;
    }
</style>
